import { Button, Stack, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";

import { ImageHeaderStyled, ImageShowStyled } from "./styled";

import { useGetImageById } from "@/api/hooks/useImage";
import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import InitLoading from "@/components/app/Loading/InitLoading";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { IImageItem } from "@/api/types/image";
import { IParams } from "@/types/params";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { routesByEntity } from "@/constants/routes-by-entity";
import { formatFileSize, getFileTypeFromMime } from "@/utils/file-formatter";

const ImageShow = () => {
    const { id } = useParams<IParams>();
    const theme = useTheme();
    const imageRef = useRef<HTMLImageElement>(null);
    const [data, setData] = useState<IImageItem>();
    const [imageDimensions, setImageDimensions] = useState<{ width: number, height: number } | null>(null);

    const listAccess = useCheckPermissionAccess(PagesAccess.IMAGE, OperationAccess.LIST);

    const { data: imageData, isLoading } = useGetImageById(Number(id));

    useEffect(() => {
        if (imageData?.data) {
            setData(imageData.data);
        }
    }, [imageData]);

    const handleImageLoad = () => {
        if (imageRef.current) {
            setImageDimensions({
                width: imageRef.current.naturalWidth,
                height: imageRef.current.naturalHeight,
            });
        }
    };

    if (isLoading) return <InitLoading />;

    if (!data) return <h5>Oops! Something Went Wrong!</h5>;

    return (
        <>
            <DashboardHeader
                title={`Show Image`}
                titleSuffix={
                    <Stack direction={"row"} spacing={2}>
                        {listAccess ? (
                            <Button component={Link} to={ROUTE_CONSTANTS.MEDIA_MANAGEMENT.IMAGES.ROOT.ABSOLUTE}>
                                Back To Image List
                            </Button>
                        ) : null}
                    </Stack>
                }
            />
            <DashboardContent>
                <ImageHeaderStyled>
                    <img
                        ref={imageRef}
                        src={data.url}
                        alt={data.title || data.originalName}
                        onLoad={handleImageLoad}
                        style={{ maxWidth: "100%", maxHeight: "600px", borderRadius: "8px", objectFit: "contain" }}
                    />
                    <h1>{data.title || data.originalName}</h1>
                </ImageHeaderStyled>
                <ImageShowStyled>
                    <div>
                        <span className="label">File Name</span>
                        <span>{data.originalName}</span>
                    </div>
                    <div>
                        <span className="label">File Type</span>
                        <span>{getFileTypeFromMime(data.mimeType)}</span>
                    </div>
                    <div>
                        <span className="label">File Size</span>
                        <span>{formatFileSize(data.size)}</span>
                    </div>
                    {imageDimensions && (
                        <div>
                            <span className="label">Resolution</span>
                            <span>
                                {imageDimensions.width} × {imageDimensions.height} pixels
                            </span>
                        </div>
                    )}
                    <div>
                        <span className="label">Upload Date & Time</span>
                        <span>{dayjs(data.createdAt).format("YYYY-MM-DD HH:mm")}</span>
                    </div>
                    {data.creator && (
                        <div>
                            <span className="label">Uploaded By</span>
                            <span>
                                {data.creator.firstName} {data.creator.lastName} ({data.creator.email})
                            </span>
                        </div>
                    )}
                    <div>
                        <span className="label">Updated At</span>
                        <span>{dayjs(data.updatedAt).format("YYYY-MM-DD HH:mm")}</span>
                    </div>
                    {data.assignedTo && data.assignedTo.length > 0 && (
                        <div>
                            <span className="label">Where Used</span>
                            <span>
                                <Stack direction="column" spacing={0.5}>
                                    {data.assignedTo.map((item, index) => {
                                        const route = routesByEntity[item.entity.toUpperCase()]?.(item.id);
                                        return (
                                            <div key={index}>
                                                {route ? (
                                                    <Link
                                                        to={route}
                                                        target="_blank"
                                                        style={{
                                                            color: theme.palette.primary.main,
                                                            textDecoration: "underline",
                                                        }}
                                                    >
                                                        {item.entity}: {item.ml_title?.en || item.title} (ID: {item.id})
                                                    </Link>
                                                ) : (
                                                    <span>
                                                        {item.entity}: {item.ml_title?.en || item.title} (ID: {item.id})
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </Stack>
                            </span>
                        </div>
                    )}
                </ImageShowStyled>
            </DashboardContent>
        </>
    );
};

export default ImageShow;
